import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Sparkles, Users, Phone, Mail } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const fmtTime = (t) => {
  if (!t || !t.includes(":")) return t || "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "pm" : "am";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")}${ap}`;
};

const WHY = [
  { icon: ShieldCheck, title: "SAFETY FIRST",
    text: "A well-maintained setup, and fully qualified, first aid-trained coaches ensure a safe and supportive environment for every child." },
  { icon: Sparkles, title: "GROWTH MINDSET",
    text: "We turn “I can't” into “I can!” with tailored coaching." },
  { icon: Users, title: "COMMUNITY SPIRIT",
    text: "A warm, welcoming atmosphere where every child is encouraged, celebrated, and supported by passionate coaches and families alike." },
];

const FAQS = [
  { q: "What age do you start?", a: "We offer After-School classes for Key Stage 1 & Key Stage 2 at select schools in Southampton!" },
  { q: "Do they need experience?", a: "Not at all - beginners start here!" },
  { q: "What should they bring?", a: "Leotard/T-Shirt and Shorts, water bottle, and a smile!" },
];

const Section = ({ id, dark = false, children }) => (
  <section id={id} className={`py-10 sm:py-14 px-4 ${dark ? "bg-primary text-white" : "bg-white text-slate-800"}`}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

const H = ({ dark = false, children }) => (
  <h2 className={`font-display text-4xl sm:text-5xl text-center mb-6 uppercase ${dark ? "text-white" : "text-primary"}`}>{children}</h2>
);

export default function Home() {
  const [classes, setClasses] = useState([]);
  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="bg-primary text-white px-4 py-14 sm:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase"
          >
            Welcome to <span className="whitespace-nowrap">Fun-Nastix</span>
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 space-y-4 text-lg sm:text-xl text-white/95 max-w-3xl mx-auto"
          >
            <p>
              At Fun-Nastix, we create a fun, supportive environment where children can grow in confidence, strength,
              and skill. Our after-school gymnastics classes are designed to help every child enjoy movement, develop
              new abilities, and feel proud of their progress.
            </p>
            <p>
              We're committed to making sure every child is safe, happy, and inspired as they take part in exciting,
              well-structured sessions led by qualified, passionate coaches.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10"
          >
            <Link to="/book-online">
              <Button data-testid="hero-book-btn"
                className="rounded-full font-display text-2xl px-12 py-7 bg-white text-primary hover:bg-white/90 hover:-translate-y-1 transition-transform shadow-xl">
                Book Now!
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ABOUT US */}
      <Section id="about">
        <H>About Us</H>
        <div className="max-w-3xl mx-auto text-center space-y-5 text-lg text-slate-700 leading-relaxed">
          <p>At Fun-Nastix, we believe in more than just gymnastics – we believe in growing confident, strong and happy children.</p>
          <p>
            Led by Level 4 Women's Artistic coach Vickie Moody - whose 30+ years of experience includes coaching at
            Dynamo Gymnastics and learning from global leaders in Moscow &amp; the USA - our fun, structured sessions
            help every child thrive.
          </p>
          <p>
            With expert guidance, a passion for teaching, and a safe, supportive environment, Fun-Nastix is where
            gymnasts grow from little jumps to big dreams.
          </p>
        </div>
        <div className="text-center mt-8">
          <Link to="/about-us">
            <Button variant="outline" data-testid="about-learn-more"
              className="rounded-full font-display text-lg border-primary text-primary hover:bg-primary hover:text-white">
              Learn More
            </Button>
          </Link>
        </div>
      </Section>

      {/* OUR PROGRAMMES */}
      <Section id="classes" dark>
        <H dark>Our Programmes</H>
        <p className="text-center font-display text-2xl sm:text-3xl mb-6">Term-Time Clubs to Fit Your Schedule</p>
        <p className="text-center text-lg text-white/90 max-w-3xl mx-auto mb-4">
          Fun-Nastix runs after-school gymnastics classes designed to help children grow in confidence, strength, and
          skill. Each session is structured, supportive, and packed with fun.
        </p>
        <p className="text-center text-white/90 underline mb-8">Classes currently run on the following days and locations:</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {classes.map((c, i) => (
            <motion.div key={c.id || i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-3xl bg-white text-slate-800 p-6 hover:-translate-y-1 hover:shadow-2xl transition-all">
              <div className="font-display text-2xl text-primary">{c.day_name || WEEKDAYS[c.day_of_week]}</div>
              <div className="mt-2 font-semibold">{c.school}</div>
              <div className="mt-1 text-slate-500">{fmtTime(c.start_time)} - {fmtTime(c.end_time)}</div>
            </motion.div>
          ))}
        </div>
        <p className="text-center text-white/90 mt-8 max-w-2xl mx-auto">
          Please note: These sessions are <b>exclusively</b> for the children attending the school the activity takes
          place. Sessions are capped to ensure quality coaching in a positive environment. Children of all abilities
          are welcome!
        </p>
        <div className="text-center mt-8">
          <Link to="/book-online">
            <Button data-testid="programmes-book-btn"
              className="rounded-full font-display text-2xl px-12 py-7 bg-white text-primary hover:bg-white/90 hover:-translate-y-1 transition-transform shadow-xl">
              Book Online!
            </Button>
          </Link>
        </div>
      </Section>

      {/* PRICES */}
      <Section id="pricing">
        <H>Prices</H>
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <div className="font-display text-6xl text-primary">£7.50<span className="text-2xl text-slate-500"> per session</span></div>
          <p className="text-lg text-slate-700 leading-relaxed">
            All our one-hour classes are independent sessions (without parents present) and cost £7.50 per session.
            Payments are made monthly through our easy-to-use online portal. The monthly fee is carefully calculated
            across the school year, taking into account bank holidays, personal holidays, and school inset days -
            ensuring fairness and consistency. Payments are spread over 11 monthly instalments (September to July) for your convenience.
          </p>
          <p className="font-semibold text-primary text-lg">Reserve your child's space now!</p>
          <Link to="/book-online">
            <Button data-testid="pricing-book-btn"
              className="rounded-full font-display text-2xl px-12 py-7 bg-primary text-white hover:bg-primary/90 hover:-translate-y-1 transition-transform shadow-xl">
              Book Now!
            </Button>
          </Link>
        </div>
      </Section>

      {/* WHY CHOOSE */}
      <Section id="why" dark>
        <H dark>Why Choose Fun-Nastix?</H>
        <div className="grid md:grid-cols-3 gap-8 mt-4">
          {WHY.map((w, i) => (
            <div key={i} className="rounded-3xl bg-white text-slate-800 p-8 text-center hover:-translate-y-1 hover:shadow-2xl transition-all">
              <w.icon className="mx-auto text-primary" size={48} />
              <h3 className="font-display text-2xl mt-4 text-primary uppercase">{w.title}</h3>
              <p className="mt-3 text-slate-600">{w.text}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq">
        <H>FAQ's</H>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}
                className="rounded-2xl border-2 border-primary/15 px-6" data-testid={`faq-${i}`}>
                <AccordionTrigger className="font-display text-xl text-primary hover:no-underline text-left">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-base">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* CONTACT */}
      <Section id="contact" dark>
        <H dark>Contact Us</H>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-4">
          <a href="tel:07977440009" data-testid="home-contact-phone"
            className="flex items-center gap-3 rounded-full bg-white text-primary px-8 py-4 font-semibold hover:-translate-y-1 transition-transform shadow-lg">
            <Phone /> 07977 440 009
          </a>
          <a href="mailto:admin@fun-nastix.co.uk" data-testid="home-contact-email"
            className="flex items-center gap-3 rounded-full bg-white text-primary px-8 py-4 font-semibold hover:-translate-y-1 transition-transform shadow-lg">
            <Mail /> ADMIN@FUN-NASTIX.CO.UK
          </a>
        </div>
      </Section>
    </PublicLayout>
  );
}
